import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Download, X } from 'lucide-react';
import { frontendDb as db } from '../lib/frontend-db';
import Header from './Header';
import Footer from './Footer';
import type { PosterItem, PosterImage } from '../lib/frontend-db';

const PosterGallery = () => {
  const { slug } = useParams();
  const [poster, setPoster] = useState<PosterItem | null>(null);
  const [images, setImages] = useState<PosterImage[]>([]);
  const [selectedImage, setSelectedImage] = useState<PosterImage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (slug) {
      loadPosterAndImages();
    }
  }, [slug]);

  const loadPosterAndImages = async () => {
    if (!slug) return;

    try {
      const posterData = await db.getPosterBySlug(slug);
      if (posterData) {
        setPoster(posterData);
        const imagesData = await db.getPosterImages(posterData.id);
        setImages(imagesData);
      } else {
        setError('Galerie nicht gefunden');
      }
    } catch (error) {
      console.error('Error loading poster and images:', error);
      setError('Fehler beim Laden der Galerie');
    } finally {
      setLoading(false);
    }
  };

  const downloadImage = (url: string, filename: string) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white dark:bg-gray-950 transition-colors duration-300">
        <Header />
        <main className="container mx-auto px-4 py-16 mt-16">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-8" />
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="aspect-square bg-gray-200 dark:bg-gray-700 rounded" />
              ))}
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error || !poster) {
    return (
      <div className="min-h-screen bg-white dark:bg-gray-950 transition-colors duration-300">
        <Header />
        <main className="container mx-auto px-4 py-16 mt-16">
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 text-center">
            <h2 className="text-xl font-semibold text-red-800 mb-2">Fehler</h2>
            <p className="text-red-600 dark:text-red-400">{error || 'Galerie nicht gefunden'}</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-gray-950 transition-colors duration-300">
      <Header />
      <main className="container mx-auto px-4 py-16 mt-16">
        <h1 className="text-3xl font-bold text-[var(--primary-color)] mb-8 dark:text-[var(--primary-color)]">{poster.title}</h1>
        
        {poster.description && (
          <p className="text-gray-700 dark:text-gray-200 mb-8 max-w-3xl">{poster.description}</p>
        )}

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {images.map((image) => (
            <div
              key={image.id}
              className="aspect-square cursor-pointer group relative"
              onClick={() => setSelectedImage(image)}
            >
              <img
                src={image.image_url}
                alt={image.title}
                className="w-full h-full object-cover rounded-lg transition-transform group-hover:scale-[1.02]"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors rounded-lg" />
            </div>
          ))}
        </div>

        {selectedImage && (
          <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4">
            <div className="relative max-w-6xl w-full">
              <div className="absolute top-4 right-4 flex gap-4 z-10">
                <button
                  onClick={() => downloadImage(selectedImage.image_url, `${selectedImage.title}.jpg`)}
                  className="bg-white/10 hover:bg-white/20 dark:bg-gray-800/50 dark:hover:bg-gray-700/50 p-2 rounded-full transition-colors"
                  title="Herunterladen"
                >
                  <Download className="text-white" size={24} />
                </button>
                <button
                  onClick={() => setSelectedImage(null)}
                  className="bg-white/10 hover:bg-white/20 dark:bg-gray-800/50 dark:hover:bg-gray-700/50 p-2 rounded-full transition-colors"
                  title="Schließen"
                >
                  <X className="text-white" size={24} />
                </button>
              </div>
              <img
                src={selectedImage.image_url}
                alt={selectedImage.title}
                className="w-full h-auto max-h-[90vh] object-contain rounded-lg"
              />
              {selectedImage.title && (
                <div className="absolute bottom-4 left-4 right-4 text-center">
                  <p className="text-white text-lg bg-black/50 dark:bg-gray-900/70 inline-block px-4 py-2 rounded">
                    {selectedImage.title}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default PosterGallery;

